/**
 * figureOrigins — 그림 생성 창에 낼 **「이 자리 그림들의 원본」 카드**를 짓는다(2026-09-06 오너 결정 A 안).
 *
 * ── 왜 필요한가 ──
 * 「사진 따라 그리기」는 사진이 있어야 한다. 생성 창은 그 사진을 **올린 이미지**로만 받고 있었는데,
 * OCR 이 그림마다 만들어 둔 원본(페이지 크롭)이 서버에 멀쩡히 있다 — 오너 지적 「원본 이미지는 OCR
 * 하면서 생성된 원본을 활용하거나 업로드한 이미지로 그리거나 할 수 있어야 되는데 지금은 직접 업로드한 것만 된다」.
 *
 * ── 무엇을 내고, 고르면 무슨 일이 나나 ──
 * OCR 원본은 문제 단위가 아니라 **그림 단위**다(계약 §1-2 `crop` · §10-2 G5 `origin_crop`). 그래서 카드도 그림마다
 * 서고, 고르면 **그 그림을 그 자리에서 다시 그린다**(재요청 — FigureModal 의 [AI 요청]과 같은 호출).
 * 새 그림으로 복사해 붙이지 않는 이유(B 안을 버린 이유): 같은 크롭이 서버에 두 번 실리고 원래 그림은 사람이
 * 지워야 한다. 원본은 그 그림의 것이므로 그 그림을 다시 그리는 편이 데이터도 동선도 하나다.
 *
 * ── 2026-09-06 두 번째: **두 대상이 한 규칙이다**(오너 지시 「검수 모달과 문제 편집의 과정이 동일해야」) ──
 * 처음 판에는 OCR 갈래가 따로 있었다 — 아무것도 싣지 않고 서버 우선순위(자체 이미지 > 페이지 크롭)에 맡겼고,
 * 크롭이 없는 그림에도 「미리보기 없음」 카드를 냈다. 그 결과 ① 교체(950)한 그림에서 카드는 페이지 크롭을 보여
 * 주는데 서버는 올린 이미지를 보고 그렸고 ② 사진이 없는 카드로 [사진 따라 그리기]가 열렸다(감사 G3·E1).
 * 이제 그림 하나에 **카드가 둘까지** 선다 — 은행·OCR 같은 순서·같은 이름이다:
 *   · 「N번째 그림 원본」   보관 원본(`original_src`) — **두 대상 모두** `use_original:true`(2026-09-06 회신 C · dev `92c98ef`).
 *                          종전에는 OCR 이 그 키를 못 받아 바이트를 `image` 로 올려 보냈다. 이제 가리키기만 한다 —
 *                          수백 KB 를 올리지 않고, 서버가 제 보관본을 그대로 본다.
 *                          보관 원본이 없으면 픽셀 그림의 자체 이미지가 곧 원본이다(아무것도 싣지 않는다 — 서버 2순위).
 *   · 「N번째 그림 교재 크롭」 OCR 당시 지면 크롭(figurePageCrop) — 원본과 다른 그림일 때 따로 선다. **바이트를 `image` 로
 *                          명시한다** — 카드가 보여 주는 것과 서버가 보는 것이 같아야 한다.
 * 어느 쪽도 없는 좌표·식 그림은 카드를 내지 않는다 — 사진이 없는데 [사진 따라 그리기]를 열면 서버가 거절한다.
 * 서버가 「원본이 없다」(`has_original:false`)고 말한 그림은 뺀다. **모르는 것으로는 막지 않는다** —
 * 키가 안 온 그림까지 빼면 서버에 있는 원본을 화면이 스스로 닫는다(figureOriginalPane 의 serverHas 와 같은 규칙).
 *
 * 순수 함수다 — 목록(GET …/figures 의 items)을 넣으면 카드를 돌려준다. 호출은 두 화면의 openFigGen 이 한다.
 */
import { figureOriginalKeeping, figurePageCrop, FIGURE_CROP_LABEL } from './figureText';
import { FIGURE_SVG_EDITOR } from '../../utils/roles';
import { parseImageDataUri } from './figureApi';

/** 카드의 키 — 재료 카드들(`text`·`upload`·`v:3`)과 한 상태(`pick`)에 섞이므로 앞머리로 가른다. */
export const FIGURE_ORIGIN_KEY_PREFIX = 'fig:';

export function figureOriginKey(field, index, part = 'origin') {
  return `${FIGURE_ORIGIN_KEY_PREFIX}${field}/${index}${part === 'crop' ? ':crop' : ''}`;
}

/** 서버가 받는 규격(png·jpeg·webp · 디코드 2MB)인 data-URI 만 실어 보낸다 — 413 을 맞게 두지 않는다. */
function sendable(uri) {
  return typeof uri === 'string' && uri !== '' && parseImageDataUri(uri).ok;
}

/**
 * @param items  `GET …/figures` 의 items(계약 §1-2). 없으면 빈 배열로 본다.
 * @param target 'ocr' | 'bank'
 * @param field  생성 창이 열린 자리('body'|'solution') — 다른 자리 그림은 내지 않는다. 창 제목이 「본문 그림」인데
 *               풀이 그림이 바뀌면 사람은 어디가 바뀌었는지 찾지 못한다.
 * @returns [{ key, field, index, label, thumb, request }] — 서버 순번 순, 같은 그림은 원본 → 교재 크롭 순.
 *   thumb 은 보기용 data-URI, request 는 재요청 body 에 얹을 것(`useOriginal` | `image` | 없음).
 */
export function listFigureOrigins(items, target, field) {
  const list = Array.isArray(items) ? items : [];
  const out = [];
  list.forEach((it) => {
    if (!it || it.field !== field) return;
    const index = Number(it.index);
    if (!Number.isFinite(index)) return;
    if (figureOriginalKeeping(it) === 'gone') return; // 서버가 없다고 한 것만 뺀다
    const n = index + 1;

    // ① 원본 — 보관 원본이 으뜸, 없으면 픽셀 그림의 자체 이미지.
    let origin = null;
    if (typeof it.original_src === 'string' && it.original_src) {
      // 두 대상 모두 `use_original:true` — 서버가 제 보관본을 본다(바이트를 올려 보내지 않는다).
      origin = { thumb: it.original_src, request: { useOriginal: true } };
    } else if (it.kind === 'pixel' && typeof it.src === 'string' && it.src && (!FIGURE_SVG_EDITOR || sendable(it.src))) {
      origin = { thumb: it.src, request: {} }; // 노드 자체가 원본 — 아무것도 싣지 않으면 서버가 그것을 쓴다
    }
    if (origin) {
      out.push({
        key: figureOriginKey(field, index), field, index, label: `${n}번째 그림 원본`, thumb: origin.thumb, request: origin.request,
      });
    }

    // ② 교재 크롭 — 원본과 **다른 그림일 때만** 따로 선다(같은 그림에 이름을 둘 붙이지 않는다). 바이트를 명시한다.
    const crop = figurePageCrop(it, target);
    if (crop && sendable(crop) && crop !== origin?.thumb) {
      out.push({
        key: figureOriginKey(field, index, 'crop'), field, index, label: `${n}번째 그림 ${FIGURE_CROP_LABEL}`, thumb: crop, request: { image: crop },
      });
    }
  });
  // 서버 순번 순, 같은 순번 안에서는 원본이 앞(위에서 넣은 차례가 그대로다 — 안정 정렬).
  out.sort((a, b) => a.index - b.index);
  return out;
}

export default listFigureOrigins;
